import { ReactNode, forwardRef, ForwardedRef, DOMAttributes } from 'react'
import styled from '@emotion/styled'
import useLayout from 'state/useLayout'
import { LayoutContextProps } from 'providers/LayoutProvider'
import PageTitle from '../PageTitle'

interface ContentLayoutProps
  extends Pick<DOMAttributes<HTMLDivElement>, 'onScroll'> {
  children?: ReactNode
  /**
   * Takes a string to output a class. Related to emotion - It lets another component style it
   */
  className?: string
  variant?: 'default' | 'clear'
}

type StyledContentLayoutProps = Pick<LayoutContextProps, 'layout' | 'table'> &
  Pick<ContentLayoutProps, 'variant'>

/**
 * Use ContentLayout to render the dominant content of the page.
 */
const ContentLayout = (
  { children, className, onScroll, variant = 'default' }: ContentLayoutProps,
  ref?: ForwardedRef<HTMLDivElement>
) => {
  const { layout, table } = useLayout()

  return (
    <StyledContentLayout className={className} ref={ref} onScroll={onScroll}>
      <ContentWrapper layout={layout} table={table} variant={variant}>
        <PageTitle location="outside-header" />
        {children}
      </ContentWrapper>
    </StyledContentLayout>
  )
}

export default forwardRef(ContentLayout)

const StyledContentLayout = styled.div(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  height: '100%',
  overflow: 'auto',
  background: theme.colors.background.primary,
  scrollbarGutter: 'stable',
  // Targeting to change the style when a modal is opened
  '[role="dialog"] &': {
    background: theme.colors.background.secondary
  }
}))

export const ContentWrapper = styled.div<StyledContentLayoutProps>(
  ({ theme }) => ({
    display: 'flex',
    flex: '1 0 auto',
    flexDirection: 'column',
    main: {
      height: '100%',
      padding: theme.space[5],
      borderRadius: theme.mixins.pxSpread([
        theme.radii[3],
        theme.radii[3],
        0,
        0
      ])
    }
  }),
  ({ theme, layout }) =>
    layout && {
      ...theme.mixins.centeredContainer(theme.layout[layout].maxWidthSize),
      [theme.mediaQueries.md]: {
        ...theme.mixins.centeredContainer(theme.layout[layout].maxWidthSize, 5)
      }
    },
  ({ theme, table }) =>
    table &&
    table.isIncluded && {
      main: {
        height: '100%',
        display: 'flex',
        padding: 0,
        background: theme.colors.background.primary
      }
    },
  ({ theme, variant }) =>
    variant === 'default' && { main: { background: theme.colors.white } },
  ({ theme, variant }) =>
    variant === 'clear' && {
      main: {
        background: 'transparent',
        [theme.mediaQueries.md]: {
          padding: theme.space[0]
        }
      }
    }
)
